import { useEffect, useMemo, useState } from 'react';
import { Loader2, Tag } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { cn } from '../../../components/ui/utils';
import { useAccountStore, type Account } from '../../../entities/account';
import { accountsApi } from '../../../shared/api/accounts.api';

interface BulkTagsDialogProps {
  mode: 'add' | 'remove' | null;
  accounts: Account[];
  /** Все тэги — подсказки при добавлении. */
  knownTags: string[];
  onOpenChange: (open: boolean) => void;
  onDone?: () => void;
}

/**
 * Тэг сразу нескольким аккаунтам. Отдельного массового метода на бэкенде нет —
 * сохраняем по одному тем же PUT, что и диалог тэгов карточки.
 */
export function BulkTagsDialog({ mode, accounts, knownTags, onOpenChange, onDone }: BulkTagsDialogProps) {
  const updateAccount = useAccountStore((state) => state.updateAccount);
  const [tag, setTag] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (mode) setTag(''); }, [mode]);

  // Убрать можно только то, что у выбранных есть.
  const presentTags = useMemo(
    () => [...new Set(accounts.flatMap((a) => a.tags ?? []))].sort((a, b) => a.localeCompare(b, 'ru')),
    [accounts],
  );
  const choices = mode === 'remove' ? presentTags : knownTags;
  const normalized = tag.trim().toLowerCase();

  const apply = async () => {
    if (!mode || !normalized) return;
    setSaving(true);
    let failed = 0;
    let changed = 0;
    for (const account of accounts) {
      const current = account.tags ?? [];
      const next = mode === 'add'
        ? (current.includes(normalized) ? current : [...current, normalized])
        : current.filter((t) => t !== normalized);
      if (next.length === current.length) continue;
      try {
        await accountsApi.update(account.id, { tags: next });
        updateAccount(account.id, { tags: next });
        changed += 1;
      } catch (error) {
        console.error('Bulk tags update failed:', account.id, error);
        failed += 1;
      }
    }
    setSaving(false);
    if (failed > 0) toast.error(`Не сохранилось у ${failed} из ${changed + failed}`);
    else toast.success(mode === 'add' ? `Тэг «${normalized}» добавлен: ${changed}` : `Тэг «${normalized}» убран: ${changed}`);
    onOpenChange(false);
    onDone?.();
  };

  return (
    <Dialog open={mode !== null} onOpenChange={(open) => !saving && onOpenChange(open)}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={(e) => { e.preventDefault(); void apply(); }} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              {mode === 'remove' ? 'Убрать тэг' : 'Добавить тэг'}
            </DialogTitle>
            <DialogDescription>Выбрано аккаунтов: {accounts.length}</DialogDescription>
          </DialogHeader>

          {mode === 'add' && (
            <Input
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="Новый или существующий тэг"
              autoFocus
            />
          )}
          {choices.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {choices.map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={normalized === t}
                  onClick={() => setTag(t)}
                  className={cn(
                    'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                    normalized === t ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent',
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          ) : mode === 'remove' ? (
            <p className="text-sm text-muted-foreground">У выбранных аккаунтов нет тэгов.</p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Отмена</Button>
            <Button type="submit" variant={mode === 'remove' ? 'destructive' : 'default'} disabled={saving || !normalized}>
              {saving && <Loader2 className="animate-spin" />}
              {mode === 'remove' ? 'Убрать' : 'Добавить'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
